// Small status primitives shared across the Billing area.
import React from 'react';
import {
  Ban,
  BellOff,
  CalendarClock,
  Mail,
  MailOpen,
  MailX,
  MousePointerClick,
} from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { fmtDate, fmtDateTime } from './billingFormat';

const STATUS_STYLES = {
  open: 'bg-status-info-bg text-status-info-fg',
  paid: 'bg-status-success-bg text-status-success-fg',
  // Indigo: drafts are work-in-progress, not silenced - grey/muted is
  // reserved for void/uncollectible and the Muted tile.
  draft: 'bg-indigo-100 text-indigo-700',
  void: 'bg-muted text-muted-foreground',
  uncollectible: 'bg-muted text-muted-foreground',
};

const STATUS_LABELS = {
  open: 'Open',
  paid: 'Paid',
  draft: 'Draft',
  void: 'Void',
  uncollectible: 'Uncollectible',
};

export const InvoiceStatusPill = ({ status }) => (
  <Badge className={STATUS_STYLES[status] || 'bg-muted text-muted-foreground'}>
    {STATUS_LABELS[status] || status}
  </Badge>
);

// Claim lifecycle text for the Invoices page CLAIM STATUS column — purely
// informational, so plain muted text (no pill; the invoice STATUS column
// keeps the colored pills). Labels mirror the claims board columns; unknown
// statuses fall back to title case.
const CLAIM_STATUS_LABELS = {
  intake: 'Intake',
  'awaiting medical records': 'Awaiting Med. Recs.',
  'in process': 'In Process',
  'awaiting internal review': 'Awaiting Review',
  'internal review approved': 'Review Approved',
  'submitted to va': 'Submitted',
  invoiced: 'Invoiced',
  'payment plan': 'Payment Plan',
  paid: 'Paid',
  'dispute hold': 'Dispute Hold',
  dead: 'Dead',
};

export const ClaimStatusText = ({ status }) => {
  const key = String(status || '').toLowerCase();
  const label =
    CLAIM_STATUS_LABELS[key] ||
    key.replace(/\b\w/g, (c) => c.toUpperCase()) ||
    '—';
  return <span className="text-sm text-muted-foreground">{label}</span>;
};

// PLAN column chip: the invoice's full plan lifecycle at a glance. Real plan
// states get a pill (violet family; past_due/failed borrow the warning/error
// tones); offered-only states (no plan record yet) render as muted text so
// they read as background info, not an alert. planState comes derived from
// the server (see listAdminInvoices) so the cell, the column filter, and the
// quick tile always agree.
export const PlanStateChip = ({
  planState,
  paidCount,
  totalCount,
  depositProcessing,
}) => {
  if (!planState) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  // Offered-only states: plain muted text, no pill.
  if (planState === 'offered' || planState === 'unlocked') {
    const offered = planState === 'offered';
    return (
      <span
        className="whitespace-nowrap text-xs text-muted-foreground"
        title={
          offered
            ? 'This invoice is old enough that the client can start a payment plan themselves from their billing page. No plan exists yet.'
            : 'Staff unlocked the payment plan option early for this claim. The client can start a plan now; no plan exists yet.'
        }
      >
        {offered ? 'Offered (auto)' : 'Unlocked by staff'}
      </span>
    );
  }

  const progress =
    paidCount != null && totalCount != null
      ? ` ${Number(paidCount)} of ${Number(totalCount)}`
      : '';
  const variant =
    planState === 'failed'
      ? {
          cls: 'bg-status-error-bg text-status-error-fg',
          label: 'Failed',
          title:
            'Installment charges have repeatedly failed. Charges keep retrying automatically each day, and reminder emails have resumed for this invoice.',
        }
      : planState === 'past_due'
        ? {
            cls: 'bg-status-warning-bg text-status-warning-fg',
            label: 'Past due',
            title:
              'An installment charge failed and is being retried automatically. The plan returns to Active as soon as a retry succeeds.',
          }
        : planState === 'requested'
          ? {
              // Outline treatment: violet plan family, but not active yet.
              cls: 'border border-violet-300 bg-violet-50 text-violet-600',
              label: 'Proposed',
              title:
                'Staff drafted a payment plan proposal. It has not been accepted by the client yet; nothing is charging. Reminder emails are paused meanwhile.',
            }
          : planState === 'setup_pending' && depositProcessing
            ? {
                // Sky, not amber: the money is moving, this is progress, not
                // a warning. Outline treatment = still the pending family.
                cls: 'border border-sky-300 bg-sky-50 text-sky-700',
                label: 'Deposit clearing',
                title:
                  'The client set up the plan and the ACH down payment is clearing at the bank, typically 3 to 5 business days. Installments start once it clears. Reminder emails stay paused.',
              }
            : planState === 'setup_pending'
              ? {
                  cls: 'border border-violet-300 bg-violet-50 text-violet-600',
                  label: 'Awaiting setup',
                  title:
                    'A checkout link exists but the client has not completed the down payment or card setup. Nothing is charging yet. Reminder emails are paused meanwhile.',
                }
              : {
                  // Violet: deliberately distinct from every status pill tone
                  // (Open=blue, Paid=green, warnings=yellow, errors=red).
                  cls: 'bg-violet-100 text-violet-700',
                  label: `Active${progress}`,
                  title:
                    'This claim is on an active payment plan. Installments pay this invoice down automatically and reminder emails are skipped.',
                };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        variant.cls,
      )}
      title={variant.title}
    >
      <CalendarClock className="h-3 w-3" /> {variant.label}
    </span>
  );
};

export const RemindersPausedChip = () => (
  <span
    // Deliberately yellow (not the amber warning token): must read clearly
    // different from the red dispute-hold bell at icon size.
    className="inline-flex items-center text-yellow-500"
    title="Reminder emails are paused for this invoice. The invoice itself is still open and payable."
  >
    <BellOff className="h-4 w-4" />
  </span>
);

// Red variant: billing is frozen automatically at the claim level - Dispute
// Hold or a dead claim (vs the yellow bell, a manual per-invoice pause).
// When red and yellow both apply, render ONLY this one - red trumps yellow.
export const DisputeMutedChip = ({ reason = 'dispute' }) => (
  <span
    className="inline-flex items-center text-status-error-fg"
    title={
      reason === 'dead'
        ? 'Billing is frozen: this claim is dead. No billing email or payment plan charges will ever be sent.'
        : 'Billing email is frozen: this claim is on Dispute Hold. No invoice emails, reminders, or payment plan charges until the hold is released.'
    }
  >
    <BellOff className="h-4 w-4" />
  </span>
);

// Exclusion-list kill switch: a different SHAPE (Ban, not a bell) because it
// is a different mechanism - address-level, blocks ALL billing email, managed
// in Billing Settings. Renders alongside a bell when both apply.
export const ExcludedChip = () => (
  <span
    className="inline-flex items-center text-slate-700"
    title="This email address is on the billing exclusion list. NO billing email of any kind will be sent. Manage the list in Billing Settings."
  >
    <Ban className="h-4 w-4" />
  </span>
);

// Compact delivery/engagement cluster for the Last Reminder column — the
// "who never opened their invoice" signal Madison wants at a glance.
export const EngagementIndicator = ({
  sentAt,
  openedAt,
  clickedAt,
  bounced,
}) => {
  if (!sentAt) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (bounced) {
    return (
      <span
        className="inline-flex items-center gap-1 text-status-error-fg"
        title="The email bounced. Check the address."
      >
        <MailX className="h-4 w-4" />
        <span className="text-xs">Bounced</span>
      </span>
    );
  }
  if (clickedAt) {
    return (
      <span
        className="inline-flex items-center gap-1 text-status-success-fg"
        title={`Clicked ${fmtDateTime(clickedAt)}`}
      >
        <MousePointerClick className="h-4 w-4" />
        <span className="text-xs">Clicked</span>
      </span>
    );
  }
  if (openedAt) {
    return (
      <span
        className="inline-flex items-center gap-1 text-status-info-fg"
        title={`Opened ${fmtDateTime(openedAt)}`}
      >
        <MailOpen className="h-4 w-4" />
        <span className="text-xs">Opened</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-muted-foreground"
      title={`Sent ${fmtDate(sentAt)}. Never opened.`}
    >
      <Mail className="h-4 w-4" />
      <span className="text-xs">Never opened</span>
    </span>
  );
};

export const Th = ({ children, className }) => (
  <th
    className={cn(
      'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground',
      className,
    )}
  >
    {children}
  </th>
);
